/**
 * PublicProposal - Landing pública da proposta comercial (rota /p/:token)
 *
 * Página sem autenticação onde o cliente visualiza a proposta (snapshot
 * congelado dos itens), baixa o PDF e registra aceite ou recusa.
 * Todo o acesso a dados passa pela Edge Function `public-proposal`.
 *
 * @version 3.0.0
 * RELEASE: 2026-09-09 v3.0.0 - Passa a montar a página pelo renderizador do Studio
 *          (blocos + blocos de ação). Sem modelo próprio, usa o modelo padrão que
 *          reproduz exatamente o layout anterior. Comportamentos preservados.
 * RELEASE: 2026-08-11 v2.0.0 - Redesign premium: hero com ancoragem de valor, escassez
 *          por validade, itens em cards, resumo financeiro, faixa de fechamento,
 *          barra fixa de CTA e diálogo de decisão repaginado. Sem mudança de dados.
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (Fase 6 do módulo de Propostas)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePublicProposal } from '@/hooks/usePublicProposal';
import { ProposalStickyBar } from '@/components/public-proposal/ProposalStickyBar';
import {
  ProposalLoadingScreen,
  ProposalRevokedScreen,
  ProposalUnavailableScreen,
} from '@/components/public-proposal/ProposalStateScreen';
import type { StudioDocument } from '@/lib/studio/core';
import { StudioWebRenderer } from '@/lib/studio/render/web/StudioWebRenderer';
import { DEFAULT_PROPOSAL_DOCUMENT } from '@/lib/studio/templates/defaultProposalTemplate';

export default function PublicProposal() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error, isSubmitting, accept, reject, trackPdfDownload } =
    usePublicProposal(token);

  // Diálogo de decisão (aceitar/recusar)
  const [decision, setDecision] = useState<'accept' | 'reject' | null>(null);
  const [actorName, setActorName] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [reason, setReason] = useState('');

  // Barra fixa de CTA aparece somente após o usuário rolar além do hero
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 420);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /** Confirma a decisão do cliente */
  const handleConfirm = async () => {
    try {
      if (decision === 'accept') {
        await accept(actorName.trim(), actorEmail.trim() || undefined);
        toast.success('Proposta aceita. Obrigado!');
      } else {
        await reject(actorName.trim(), reason.trim() || undefined, actorEmail.trim() || undefined);
        toast.success('Resposta registrada.');
      }
      setDecision(null);
      setReason('');
    } catch (err: any) {
      toast.error(err?.message || 'Não foi possível registrar sua resposta.');
    }
  };

  /** Abre o PDF em nova aba registrando o evento de download */
  const handleDownloadPdf = () => {
    if (!data?.pdf_url) return;
    void trackPdfDownload();
    window.open(data.pdf_url, '_blank', 'noopener,noreferrer');
  };

  // Documento do Studio: o da proposta, quando houver, ou o modelo padrão
  const studioDocument = useMemo<StudioDocument>(
    () => (data?.studio_document as StudioDocument | undefined) ?? DEFAULT_PROPOSAL_DOCUMENT,
    [data?.studio_document],
  );

  if (isLoading) return <ProposalLoadingScreen />;
  if (error || !data) return <ProposalUnavailableScreen message={error} />;

  // Link revogado (proposta substituída/cancelada): não exibe conteúdo comercial
  if (data.revoked) {
    return (
      <ProposalRevokedScreen
        reason={data.revoked_reason ?? 'disabled'}
        proposalNumber={data.proposal?.proposal_number}
        tenantName={data.tenant?.name}
        successor={data.successor ?? null}
      />
    );
  }

  const { proposal, tenant } = data;
  const currency = proposal.currency || 'BRL';
  const proposalTitle = proposal.title || `Proposta ${proposal.proposal_number}`;

  return (
    <div className="proposal-page min-h-screen px-4 py-8 pb-28 md:py-14 md:pb-14 md:pt-24">
      {/* Barra fixa com ancoragem do total (apenas quando ainda é possível responder) */}
      {proposal.is_actionable && (
        <ProposalStickyBar
          visible={showSticky}
          tenantName={tenant.name}
          logoUrl={tenant.logo_url}
          proposalTitle={proposalTitle}
          total={proposal.total}
          currency={currency}
          onAccept={() => setDecision('accept')}
        />
      )}

      <main className="mx-auto max-w-4xl space-y-5">
        {/* Conteúdo montado pelo renderizador do Studio (blocos + blocos de ação) */}
        <StudioWebRenderer
          document={studioDocument}
          html={data.studio_html ?? null}
          context={{
            proposal: data,
            actions: {
              onAccept: () => setDecision('accept'),
              onReject: () => setDecision('reject'),
              onDownloadPdf: handleDownloadPdf,
            },
          }}
        />
      </main>


      {/* Diálogo de confirmação da decisão */}
      <Dialog open={!!decision} onOpenChange={(open) => !open && setDecision(null)}>
        <DialogContent className="proposal-page max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {decision === 'accept' ? 'Confirmar aceite da proposta' : 'Recusar proposta'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {decision === 'accept' && (
              <p className="rounded-xl bg-secondary px-3.5 py-2.5 text-xs text-secondary-foreground">
                Você está aceitando <strong>{proposalTitle}</strong>. O aceite tem validade
                jurídica e será registrado eletronicamente.
              </p>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Seu nome completo *</Label>
              <Input
                value={actorName}
                maxLength={160}
                placeholder="Nome de quem está respondendo"
                onChange={(e) => setActorName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">E-mail (opcional)</Label>
              <Input
                type="email"
                value={actorEmail}
                maxLength={200}
                placeholder="email@empresa.com"
                onChange={(e) => setActorEmail(e.target.value)}
              />
            </div>
            {decision === 'reject' && (
              <div className="space-y-1.5">
                <Label className="text-xs">Motivo (opcional)</Label>
                <Textarea
                  value={reason}
                  maxLength={1000}
                  rows={3}
                  placeholder="Conte brevemente o motivo da recusa"
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Ao confirmar, registramos data, hora e identificação técnica do acesso para fins de
              auditoria.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDecision(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={isSubmitting || actorName.trim().length < 2}
              onClick={handleConfirm}
            >
              {isSubmitting && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              {decision === 'accept' ? 'Confirmar aceite' : 'Confirmar recusa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
